import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "frozenOffsets", "zebraStripe", "lastColumnWidth", "minWidth", "condensed"];
import styled from 'foundations-theming/utils';
import { TABLE_TOP_BAR_HEIGHT, TABLE_PAGINATOR_HEIGHT, TABLE_NON_EMPTY_MIN_CONTENT_HEIGHT, SELECTION_COLUMN_WIDTH } from '../constants/sizes';
import { forwardRef } from 'react';
import { ScrollOverhang } from 'foundations-components/transitional/components/ScrollOverhang';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Table } from 'foundations-components/transitional/components/Table';
import { useAuth } from '../../auth/context/AuthContext';
import { gate } from 'hub-http/gates';
import { jsx as _jsx } from "react/jsx-runtime";
const ScrollWindow = styled.div.withConfig({
  displayName: "StyledTableComponents__ScrollWindow",
  componentId: "sc-1he06tg-0"
})(["overflow:auto;max-width:100%;max-height:100%;"]);
const FDT_BORDER_COLOR_TOKEN = 'border-core-subtle';
// The ScrollOverhang component adds `flex: 1 1 auto` to its wrapper, which causes content overflow.
// This Flex container prevents that by using column layout with specific constraints (minInlineSize="0"
// and flexGrow={0}) to maintain proper scrolling behavior without expanding beyond its boundaries.
// See: https://git.hubteam.com/HubSpot/foundations-components/issues/3950
export const TableScrollContainer = /*#__PURE__*/forwardRef(({
  children
}, ref) => /*#__PURE__*/_jsx(Flex, {
  flexDirection: "column",
  inlineSize: "100%",
  blockSize: "100%",
  minInlineSize: "0",
  flexGrow: 0,
  children: /*#__PURE__*/_jsx(ScrollOverhang, {
    children: /*#__PURE__*/_jsx(ScrollWindow, {
      ref: ref,
      children: children
    })
  })
}));
export function TableBox(props) {
  const {
    loading,
    showPagination,
    showTopBar
  } = props;
  const maxBlockSize = `calc(100% ${showTopBar ? ` - ${TABLE_TOP_BAR_HEIGHT}` : ''} ${showPagination ? ` - ${TABLE_PAGINATOR_HEIGHT}` : ''})`;
  const minBlockSize = loading ? `${TABLE_NON_EMPTY_MIN_CONTENT_HEIGHT}px` : undefined;
  return /*#__PURE__*/_jsx(View, {
    borderColor: FDT_BORDER_COLOR_TOKEN,
    borderStyle: "solid",
    borderWidth: "100",
    inlineSize: "100%",
    maxBlockSize: maxBlockSize,
    minBlockSize: minBlockSize,
    position: "relative",
    children: props.children
  });
}
export const STYLED_TABLE_COMPONENTS_FROZEN_CELL_Z_INDEX = 2;
const TableContainer = styled.div.withConfig({
  displayName: "StyledTableComponents__TableContainer",
  componentId: "sc-1he06tg-1"
})(["--standard-border-width:", ";--heavy-border-width:", ";--border-color:", ";table{border-width:0;table-layout:fixed;min-inline-size:", "px;border-collapse:separate;border-spacing:0;", " thead th[data-selection-column='true'],tbody td[data-selection-column='true']{inline-size:", "px;padding:0;", "}thead{position:sticky;inset-block-start:0;z-index:4;[data-row-type='select-query-row']{background-color:", ";block-size:", ";td{border-bottom:var(--standard-border-width) solid var(--border-color);&[data-cell-type='select-query']{overflow:visible;white-space:nowrap;", ";}}}th{border-width:0;padding:0;border-left:var(--standard-border-width) solid var(--border-color);border-bottom:var(--standard-border-width) solid var(--border-color);opacity:1;overflow-wrap:anywhere;word-break:normal;&:last-of-type{min-width:", "px;width:auto;}&:first-of-type{border-left-width:0;}}}tbody{td{", " padding-block:", ";border-width:0;border-left:var(--standard-border-width) solid ", ";border-bottom:var(--standard-border-width) solid var(--border-color);background-color:", ";&:first-of-type{border-left-width:0;}&[data-editable-cell='true']{cursor:text;transition:box-shadow 0s;[data-pressed='true']{box-shadow:inset 0 0 0 ", " ", ";}[data-invalid='true']{box-shadow:inset 0 0 0 ", " ", ";}&:hover,&:focus{box-shadow:inset 0 0 0 ", " ", ";transition:box-shadow 0.1s ease-in;}}}tr{&:hover{td{background-color:", ";}}", " &:last-of-type{td{border-bottom-width:0;}}}}}"], ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.borderWidth['300'], ({
  theme
}) => theme.color[FDT_BORDER_COLOR_TOKEN], ({
  minWidth
}) => minWidth, ({
  frozenOffsets
}) => frozenOffsets === null || frozenOffsets === void 0 ? void 0 : frozenOffsets.map((offset, index) => `
      thead th[data-column-index='${index}'], tbody td[data-column-index='${index}'] {
        position: sticky;
        z-index: ${STYLED_TABLE_COMPONENTS_FROZEN_CELL_Z_INDEX};
        inset-inline-start: ${offset}px;

        /* Set heavy border on last frozen column */
        ${index === frozenOffsets.length - 1 && `
          border-right-width: var(--heavy-border-width);

          /* Disable left border on the column directly after the last frozen column
          (otherwise it stacks with the heavy border) */
          & + td, & + th {
            border-left-width: 0;
          }
        `}
      }
      `), SELECTION_COLUMN_WIDTH, ({
  frozenOffsets
}) => frozenOffsets !== null && frozenOffsets !== void 0 && frozenOffsets.length ? `
          inset-inline-start: 0;
          position: sticky;
          z-index: ${STYLED_TABLE_COMPONENTS_FROZEN_CELL_Z_INDEX};
        ` : '', ({
  theme
}) => theme.color['fill-info-subtle'], ({
  theme
}) => theme.space['1000'], ({
  frozenOffsets
}) => frozenOffsets !== null && frozenOffsets !== void 0 && frozenOffsets.length ? `position: sticky; inset-inline-start: 0;` : '', ({
  lastColumnWidth
}) => lastColumnWidth, ({
  $condensed,
  theme
}) => $condensed ? `block-size: ${theme.space['1000']};` : `min-block-size: ${theme.space['1000']};`, ({
  theme
}) => theme.space['50'], ({
  $noVerticalBorders
}) => $noVerticalBorders ? 'transparent' : `var(--border-color)`, ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => theme.space['25'], ({
  theme
}) => theme.color['fill-info-default'], ({
  theme
}) => theme.space['25'], ({
  theme
}) => theme.color['fill-alert-default'], ({
  theme
}) => theme.space['25'], ({
  theme
}) => theme.color['border-core-hover'], ({
  theme
}) => theme.color['fill-tertiary-hover'], ({
  theme,
  zebraStripe = false
}) => {
  if (zebraStripe) {
    return `
          &:nth-child(even) {
            background-color: ${theme.color['fill-field-default']};
          }
          `;
  }
});
export const StyledTable = _ref => {
  let {
      children,
      frozenOffsets,
      zebraStripe,
      lastColumnWidth,
      minWidth,
      condensed
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    hasGate
  } = useAuth();
  const noVerticalBorders = hasGate(gate('trellis:primary'));
  return /*#__PURE__*/_jsx(TableContainer, {
    $condensed: condensed,
    frozenOffsets: frozenOffsets,
    lastColumnWidth: lastColumnWidth,
    minWidth: minWidth,
    zebraStripe: zebraStripe,
    $noVerticalBorders: noVerticalBorders,
    children: /*#__PURE__*/_jsx(Table, Object.assign({
      condensed: condensed
    }, rest, {
      children: children
    }))
  });
};
export const NoFirstDivChildPadding = styled.div.withConfig({
  displayName: "StyledTableComponents__NoFirstDivChildPadding",
  componentId: "sc-1he06tg-2"
})(["& > div:first-child{padding:0;}"]);